import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  DestructiveIcon,
  Divider,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
} from '@modusign/design-system';
import { ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import { useSelectionValues } from '@hooks';

import { modalClose, modalOpen } from '@app/store/modal/actions';

import './FileOpenPasswordPrecautionsModal.scss';

type AgreementCheckItem = 'PDF_PASSWORD_IS_SET' | 'NEED_TO_MEMO' | 'CANNOT_EXPORT_INPUT_VALUES';

const agreementCheck = ['PDF_PASSWORD_IS_SET', 'NEED_TO_MEMO', 'CANNOT_EXPORT_INPUT_VALUES'];

function FileOpenPasswordPrecautionsModal() {
  const dispatch = useDispatch();
  const { toggle, selected } = useSelectionValues<AgreementCheckItem>();

  const createAgreementCheckBoxProps = (checkOrder: AgreementCheckItem) => {
    return {
      id: `file-open-password-modal-${checkOrder}`,
      checked: selected.includes(checkOrder),
      onClickCheckBox: () => toggle(checkOrder),
    };
  };

  return (
    <Modal
      isOpen
      maxWidth="sm"
      onClose={() => {
        dispatch(modalClose());
      }}
    >
      <ModalTitle icon={<DestructiveIcon color="iconPrimaryBase" />}>
        완료 문서 잠금 설정
      </ModalTitle>
      <ModalContent mb={6}>
        <Flex justifyContent="center">
          <Text fontSize="md" fontWeight="regular">
            잠금 설정 전 주의사항을 꼭 확인해주세요.
          </Text>
        </Flex>
        <Divider my={7} />
        <Flex flexDirection="column">
          <TitleWithCheckBox {...createAgreementCheckBoxProps('PDF_PASSWORD_IS_SET')}>
            서명이 모두 완료된 PDF 문서에 비밀번호가 설정됩니다.
          </TitleWithCheckBox>
          <Text color="textNeutralLight" fontSize="sm" fontWeight="regular" mb={5} mt={1}>
            비밀번호를 입력해야 완료 문서와 감사추적인증서를 확인할 수 있습니다.
          </Text>
          <Box
            bg="boxBackgroundPrimaryBase"
            borderColor="boxBorderPrimaryBase"
            borderStyle="solid"
            borderWidth={1}
            px={6}
            py={7}
          >
            서명자, 참조자, 진행자에게 설정한 비밀번호를 꼭 전달해 주세요.
          </Box>
        </Flex>
        <Divider my={7} />
        <Flex>
          <TitleWithCheckBox {...createAgreementCheckBoxProps('NEED_TO_MEMO')}>
            비밀번호는 요청자 본인만 알 수 있으니, 별도로 기록해 두세요.
          </TitleWithCheckBox>
        </Flex>
        <Divider my={7} />
        <Flex>
          <TitleWithCheckBox {...createAgreementCheckBoxProps('CANNOT_EXPORT_INPUT_VALUES')}>
            잠금 설정된 문서는 입력값 추출이 불가능합니다.
          </TitleWithCheckBox>
        </Flex>
        <Divider my={7} />
      </ModalContent>
      <ModalFooter>
        <Flex gap="8px" justifyContent="center">
          <Button
            onClick={() => {
              dispatch(modalClose());
            }}
          >
            취소
          </Button>
          <Button
            disabled={selected.length !== agreementCheck.length}
            variant="primary"
            onClick={() => {
              dispatch(modalClose());
              dispatch(modalOpen('EDITOR_CONTRACT_SETUP_PREVIEW_MODAL'));
            }}
          >
            확인
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}

export default FileOpenPasswordPrecautionsModal;

const TitleWithCheckBox = ({
  id,
  children,
  checked,
  onClickCheckBox,
}: {
  id: string;
  children: ReactNode;
  checked: boolean;
  onClickCheckBox: () => void;
}) => (
  <Flex alignItems="center" justifyContent="space-between" width="100%">
    <Box fontSize="md" fontWeight="medium">
      <FontAwesomeIcon icon={faCheck} />
      <Text fontSize="md" fontWeight="medium" ml={1}>
        {children}
      </Text>
    </Box>
    <Flex alignItems="center" justifyContent="center">
      <Text
        as="label"
        color="textNeutralSlightlyLight"
        fontSize="md"
        fontWeight="medium"
        htmlFor={id}
        mr={2}
        sx={{
          cursor: 'pointer',
        }}
      >
        확인
      </Text>
      <Checkbox checked={checked} id={id} weight="sm" onClick={onClickCheckBox} />
    </Flex>
  </Flex>
);
